import { t } from "@lingui/macro";
import {
  ArrowsInIcon,
  CornersOutIcon,
  CrosshairSimpleIcon,
  MinusIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { Button, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

export type RightPanelProps = {
  previewSlot: ReactNode;
  zoom?: number;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onFit?: () => void;
  onCenter?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  statusSlot?: ReactNode;
};

export const RightPanel = ({
  previewSlot,
  zoom = 1,
  onZoomIn,
  onZoomOut,
  onFit,
  onCenter,
  onToggleFullscreen,
  isFullscreen = false,
  statusSlot,
}: RightPanelProps) => {
  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col",
        isFullscreen && "fixed inset-0 z-50 bg-muted/40",
      )}
    >
      {/* Toolbar */}
      <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border bg-background/70 px-4 backdrop-blur">
        <div className="flex items-center">{statusSlot}</div>

        <div className="flex items-center gap-0.5 rounded-full border border-border bg-background p-0.5">
          <Tooltip content={t`Zoom out`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-full"
              aria-label={t`Zoom out`}
              onClick={onZoomOut}
            >
              <MinusIcon size={14} />
            </Button>
          </Tooltip>
          <button
            type="button"
            className="min-w-[46px] rounded-sm px-1 font-mono text-xs tabular-nums text-foreground hover:bg-secondary"
            aria-label={t`Fit to width`}
            onClick={onFit}
          >
            {Math.round(zoom * 100)}%
          </button>
          <Tooltip content={t`Zoom in`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-full"
              aria-label={t`Zoom in`}
              onClick={onZoomIn}
            >
              <PlusIcon size={14} />
            </Button>
          </Tooltip>

          <span aria-hidden className="mx-1 h-4 w-px bg-border" />

          <Tooltip content={t`Fit to width`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-full"
              aria-label={t`Fit to width`}
              onClick={onFit}
            >
              <ArrowsInIcon size={14} />
            </Button>
          </Tooltip>
          <Tooltip content={t`Center`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-full"
              aria-label={t`Center`}
              onClick={onCenter}
            >
              <CrosshairSimpleIcon size={14} />
            </Button>
          </Tooltip>
          <Tooltip content={t`Fullscreen`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-full"
              aria-label={t`Fullscreen`}
              aria-pressed={isFullscreen}
              onClick={onToggleFullscreen}
            >
              {isFullscreen ? <ArrowsInIcon size={14} /> : <CornersOutIcon size={14} />}
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Stage — the artboard iframe handles its own zoom/pan internally */}
      <div className="relative min-h-0 flex-1 overflow-hidden bg-muted/30">{previewSlot}</div>

      {isFullscreen ? (
        <Button
          variant="primary"
          className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 gap-2 rounded-full shadow-lg"
          aria-label={t`Exit fullscreen`}
          onClick={onToggleFullscreen}
        >
          <ArrowsInIcon size={14} />
          {t`Exit fullscreen`}
        </Button>
      ) : null}
    </div>
  );
};
